import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL, ROLE_SUPERVISOR, ROLE_SUPERADMIN } from '../constants/property';

const AuthContext = createContext();

export const useAuth = () => {

  const context = useContext(AuthContext);

  if (!context) {

    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  console.log('AuthProvider rendering, isLoading:', isLoading);

  // Load user data from localStorage on mount
  useEffect(() => {
    const loadUserData = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('authToken');
        
        if (storedUser && storedToken) {
          const userData = JSON.parse(storedUser);
          console.log('Loading user from localStorage:', userData);
          console.log('Loading token from localStorage:', storedToken);
          setUser(userData);
          setAccessToken(storedToken);
          console.log('User state set to:', userData);
          console.log('Token state set to:', storedToken);
        } else {
          console.log('No stored user or token found');
          console.log('storedUser:', storedUser);
          console.log('storedToken:', storedToken);
        }
      } catch (error) {
        console.error('Error loading user data from localStorage:', error);
        // Clear corrupted data
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, []);

  // Login function
  const login = async (username, password) => {
    try {
    const response = await fetch(`${API_BASE_URL}/api/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          password: password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();
      console.log('=== AUTHCONTEXT LOGIN DEBUG ===');
      console.log('API Response data:', data);
      console.log('Looking for token field:', data.token || data.accessToken);
      
      // Store user data and token - check both possible token fields
      const token = data.token || data.accessToken || data.access_token;
      if (token) {
        // Build user object from response (which includes roles)
        const userData = data.user || {
          id: data.id,
          username: data.username || username,
          email: data.email,
          roles: data.roles || [],
          firstName: data.firstName,
          lastName: data.lastName,
          employeeName: data.employeeName
        };
        
        // Update state
        console.log('Setting user state:', userData);
        console.log('Setting accessToken state:', token);
        setUser(userData);
        setAccessToken(token);
        
        // Store in localStorage
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(userData));
        console.log('Stored in localStorage - authToken:', localStorage.getItem('authToken'));
        console.log('Stored in localStorage - user:', localStorage.getItem('user'));
        
        console.log('Login successful:', userData);
        console.log('Token stored:', token);
        console.log('=== END AUTHCONTEXT LOGIN DEBUG ===');
        
        return { success: true, user: userData, token: token };
      }
      
      console.log('No token found in response:', data);
      throw new Error('No token received from server');
      
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout function
  const logout = () => {
    // Clear state
    setUser(null);
    setAccessToken(null);
    
    // Clear localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    
    console.log('User logged out');
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return user && accessToken;
  };

  // Role helpers
  const hasRole = (role) => {
    return user?.roles?.includes(role);
  };

  const hasAnyRole = (roles = []) => {
    return roles.some((role) => user?.roles?.includes(role));
  };

  // Get current user
  const getCurrentUser = () => {
    return user;
  };

  // Get current token
  const getAccessToken = () => {
    console.log('getAccessToken called, current accessToken:', accessToken);
    return accessToken;
  };

  const value = {
    user,
    accessToken,
    isLoading,
    login,
    logout,
    isAuthenticated,
    getCurrentUser,
    getAccessToken,
    hasRole,
    hasAnyRole,
    ROLE_SUPERVISOR,
    ROLE_SUPERADMIN
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
